import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useI18n } from './i18n/index.tsx';
import { api } from './api/client.ts';
import type { Session } from './api/types.ts';
import { Shell, type Route } from './components/Shell.tsx';
import { Notice, Skeleton } from './components/ui.tsx';
import { SignIn } from './screens/SignIn.tsx';
import { Access } from './screens/Access.tsx';
import { Dashboard } from './screens/Dashboard.tsx';
import { Servers } from './screens/Servers.tsx';
import { Deployments } from './screens/Deployments.tsx';
import { DeploymentScreen } from './screens/DeploymentScreen.tsx';
import { Wizard } from './wizard/Wizard.tsx';
import { Backups } from './screens/Backups.tsx';
import { Monitoring } from './screens/Monitoring.tsx';
import { Settings } from './screens/Settings.tsx';
import { Admin } from './screens/Admin.tsx';
import { parseRoute, routeToPath } from './routing.ts';
import { SessionProvider } from './session/index.tsx';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';
import { Button } from './components/ui.tsx';

/**
 * The application root: session, routing, and which screen to show.
 *
 * The session is resolved from the cookie on first load rather than kept in
 * storage, so a customer who signed in yesterday lands on their dashboard and
 * one whose session has expired lands on sign-in — without this code having to
 * reason about expiry itself.
 */
export function App(): ReactNode {
  const { t } = useI18n();
  const [session, setSession] = useState<Session | undefined>();
  const [loading, setLoading] = useState(true);
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));
  const [error, setError] = useState<string | undefined>();
  // `navigator.onLine` is a hint, not a fact — it says the device has a
  // network, not that our server is reachable. It is still worth showing,
  // because a customer with aeroplane mode on deserves a better explanation
  // than a request that quietly fails.
  const [offline, setOffline] = useState(() => !navigator.onLine);

  useEffect(() => {
    const goOnline = (): void => setOffline(false);
    const goOffline = (): void => setOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        setSession(await api.currentSession());
      } catch {
        setError(t('error.network'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Any request may discover the session is gone; one place handles it.
  useEffect(() => api.subscribeUnauthenticated(() => setSession(undefined)), []);

  useEffect(() => {
    const onPopState = (): void => setRoute(parseRoute(window.location.pathname));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const navigate = useCallback((next: Route) => {
    const path = routeToPath(next);
    if (path !== window.location.pathname) window.history.pushState({}, '', path);
    setRoute(next);
    // A new screen starts at its top; carrying the old scroll position over is
    // disorienting, particularly on a phone.
    window.scrollTo({ top: 0 });
  }, []);

  const signOut = useCallback(async () => {
    await api.signOut().catch(() => undefined);
    setSession(undefined);
    navigate({ name: 'dashboard' });
  }, [navigate]);

  // Before the session is even resolved: a one-time link from the bot belongs
  // to somebody who has no account, and sending them to a sign-in form would
  // be exactly the dead end this page exists to remove.
  if (route.name === 'access') return <Access token={route.token} />;

  if (loading) {
    return (
      <div className="boot">
        <Skeleton lines={3} />
      </div>
    );
  }

  if (!session) {
    return (
      <>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <SignIn
          onSignedIn={(next) => {
            setSession(next);
            navigate({ name: 'dashboard' });
          }}
        />
      </>
    );
  }

  return (
    <SessionProvider session={session}>
      <Shell session={session} route={route} onNavigate={navigate} onSignOut={() => void signOut()}>
        {offline ? <Notice tone="warning">{t('common.offline')}</Notice> : null}
        {/* Keyed on the route, so a screen that failed does not keep failing
            on the next one the customer asks for. */}
        <ErrorBoundary
          resetKey={routeToPath(route)}
          fallback={(retry) => (
            <Notice
              tone="danger"
              title={t('error.screenTitle')}
              actions={
                <>
                  <Button variant="secondary" onClick={retry}>
                    {t('common.retry')}
                  </Button>
                  <Button variant="ghost" onClick={() => navigate({ name: 'dashboard' })}>
                    {t('nav.dashboard')}
                  </Button>
                </>
              }
            >
              {t('error.screenBody')}
            </Notice>
          )}
        >
          {route.name === 'dashboard' ? (
            <Dashboard session={session} onNavigate={navigate} />
          ) : null}
          {route.name === 'servers' ? <Servers /> : null}
          {route.name === 'addServer' ? (
            <Servers startAdding onChanged={() => navigate({ name: 'servers' })} />
          ) : null}
          {route.name === 'deployments' ? (
            <Deployments
              onOpen={(id) => navigate({ name: 'deployment', id })}
              onStart={() => navigate({ name: 'newDeployment' })}
            />
          ) : null}
          {route.name === 'newDeployment' ? (
            <Wizard
              onStarted={(id) => navigate({ name: 'deployment', id })}
              onAddServer={() => navigate({ name: 'addServer' })}
            />
          ) : null}
          {route.name === 'deployment' ? <DeploymentScreen deploymentId={route.id} /> : null}
          {route.name === 'backups' ? <Backups /> : null}
          {route.name === 'monitoring' ? <Monitoring /> : null}
          {route.name === 'settings' ? <Settings session={session} /> : null}
          {/* Rendered only for a session that carries it. The server refuses
              anybody else, so this is about not showing a screen that would be
              nothing but errors. */}
          {route.name === 'admin' && session.platformAdmin ? (
            <Admin
              section={route.section}
              {...(route.customerId ? { customerId: route.customerId } : {})}
              onNavigate={(section, customerId) =>
                navigate({ name: 'admin', section, ...(customerId ? { customerId } : {}) })
              }
            />
          ) : null}
        </ErrorBoundary>
      </Shell>
    </SessionProvider>
  );
}
